import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

/**
 * Backwards-compatible replacement for React.useDeferredValue.
 * - Uses React.useDeferredValue if available (React 18+)
 * - Falls back to a small timeout to yield the main thread on older React versions
 */
export function useDeferredValueCompat<T>(value: T, options?: { delayMs?: number }): T {
    const delayMs = options?.delayMs ?? 0
    const reactUseDeferred = (React as any).useDeferredValue as undefined | (<U>(value: U) => U)

    if (typeof reactUseDeferred === 'function') {
        return reactUseDeferred(value)
    }

    const [deferred, setDeferred] = useState<T>(value)
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
            setDeferred(value)
        }, delayMs)
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
                timeoutRef.current = null
            }
        }
    }, [value, delayMs])

    return deferred
}

